import React from "react";
import { Descendant } from "slate";
import { Slate, Editable } from "slate-react";
import { ManualEditorProps } from "components/ManualBuilder/types";
import { Toolbar } from "components/ManualBuilder/components/toolbar";
import { useTranslation } from "react-i18next";
import { useEditorConfig } from "./hooks/useEditorConfig";
import { useKeyboardHandlers } from "./hooks/useKeyboardHandlers";
import { useEditorRenderers } from "./hooks/useEditorRenderers";
import {
  PDF_CONTENT_WIDTH_PX,
  PDF_PAGE_X_PADDING_PX,
  PDF_PAGE_OUTER_WIDTH_PX,
  ZOOM_OPTIONS,
  ZoomLevel,
  MIN_FIT_ZOOM,
} from "components/ManualBuilder/constants";

const initialValue: Descendant[] = [
  {
    type: "paragraph",
    children: [{ text: "Start typing here..." }],
  },
];

export const ManualEditor = (props: ManualEditorProps) => {
  const { t } = useTranslation();
  const editor = useEditorConfig();
  const onKeyDown = useKeyboardHandlers(editor);
  const { renderElement, renderLeaf } = useEditorRenderers();

  // Zoom: "fit" auto-scales the page to the canvas width (<=100%); numbers are
  // fixed scales. Applied via the CSS `zoom` property so wrapping fidelity and
  // the contenteditable caret/selection stay correct.
  const [zoom, setZoom] = React.useState<ZoomLevel>("fit");
  const [fitFactor, setFitFactor] = React.useState(1);
  const canvasRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const recompute = () => {
      // Available width inside the canvas padding.
      const style = window.getComputedStyle(canvas);
      const paddingX =
        parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
      const available = canvas.clientWidth - paddingX;
      const factor = Math.min(1, available / PDF_PAGE_OUTER_WIDTH_PX);
      setFitFactor(Math.max(MIN_FIT_ZOOM, factor));
    };

    recompute();
    const observer = new ResizeObserver(recompute);
    observer.observe(canvas);
    return () => observer.disconnect();
  }, []);

  const effectiveZoom = zoom === "fit" ? fitFactor : zoom;
  const [value, setValue] = React.useState<Descendant[]>(
    props.defaultValue || initialValue,
  );
  const initialValueRef = React.useRef<Descendant[]>(
    props.defaultValue || initialValue,
  );
  const saveTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // Update the initial value ref when defaultValue changes (new chapter selected)
  React.useEffect(() => {
    initialValueRef.current = props.defaultValue || initialValue;
  }, [props.defaultValue]);

  if (!props.defaultValue?.length) {
    return <>Wähle zunächst ein Kapitel aus der Liste</>;
  }

  const handleBlur = () => {
    props.onBlur?.(value);
  };

  const handleChange = (content: Descendant[]) => {
    setValue(content);
    // Check if content has changed from initial value
    const hasChanged =
      JSON.stringify(content) !== JSON.stringify(initialValueRef.current);
    props.onChange?.(content, hasChanged);

    // Trigger debounced save on change (200ms)
    if (hasChanged) {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(() => {
        props.onBlur?.(content);
      }, 200);
    }
  };

  return (
    <div>
      <div
        className="rounded-md shadow relative overflow-hidden border border-gray-200"
        style={{ height: "75vh" }}
      >
        <Slate
          editor={editor}
          onChange={handleChange}
          initialValue={props.defaultValue || initialValue}
        >
          <div className="sticky top-0 z-10 bg-white border-b flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <Toolbar />
            </div>
            <div className="flex items-center gap-2 pr-3 py-2 shrink-0">
              <label htmlFor="manual-zoom" className="text-sm text-gray-500">
                {t("manual.zoom.label")}
              </label>
              <select
                id="manual-zoom"
                value={String(zoom)}
                onChange={(e) =>
                  setZoom(
                    e.target.value === "fit"
                      ? "fit"
                      : (Number(e.target.value) as ZoomLevel),
                  )
                }
                className="text-sm rounded-md border-gray-300 focus:ring-cyan-500 focus:border-cyan-500"
              >
                {ZOOM_OPTIONS.map((option) => (
                  <option key={String(option.value)} value={String(option.value)}>
                    {t(option.labelKey)}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {/* Document canvas: a neutral workspace that frames the page sheet. */}
          <div
            ref={canvasRef}
            className="overflow-auto h-[calc(100%-48px)] bg-gray-100 py-10 px-6"
          >
            {/* Track is at least as wide as the (zoomed) sheet so the page stays
                centered when it fits and fully scrollable (not clipped) when it
                doesn't. */}
            <div className="min-w-fit flex justify-center">
              {/* White A4-style page. Its inner text column is fixed to the PDF
                  width so editor line-wrapping matches the wkhtmltopdf output;
                  the surrounding padding represents the print margins. CSS `zoom`
                  scales the whole sheet without affecting the 750px wrapping. */}
              <div
                className="bg-white shadow-lg ring-1 ring-black/5 py-[72px]"
                style={{
                  flex: "none",
                  paddingLeft: PDF_PAGE_X_PADDING_PX,
                  paddingRight: PDF_PAGE_X_PADDING_PX,
                  zoom: effectiveZoom,
                }}
              >
                <div style={{ width: PDF_CONTENT_WIDTH_PX }}>
                  <Editable
                    renderElement={renderElement}
                    renderLeaf={renderLeaf}
                    placeholder="Start typing..."
                    className="min-h-[900px] focus:outline-none"
                    onKeyDown={onKeyDown}
                    onBlur={handleBlur}
                  />
                </div>
              </div>
            </div>
          </div>
        </Slate>
      </div>
    </div>
  );
};
